import React from 'react';
import PropTypes from 'prop-types';
import Button from '../button';
import Icon from '../icon';
import useLocalStorage from '~/modules/hooks/use-localstorage';
import {
	Prompt, PromptBody, PromptDismiss, PromptText, PromptTitle
} from './notifications-panel.css';

export const DISMISSED_KEY = 'desktopNotificationsPromptDismissed';

function DesktopNotificationsPrompt(props) {
	const {
		permission,
		isSupported = false,
		isRequesting = false,
		onEnable
	} = props;

	const [isDismissed, setIsDismissed] = useLocalStorage(DISMISSED_KEY, false);

	const handleEnable = async() => {
		const result = await onEnable?.();

		// A permission still 'default' after the ask means the user closed the
		// browser's own dialog without deciding. Browsers count repeat prompts
		// against the site, so treat that as the answer it is and stop offering
		// rather than re-arming the button.
		if( result === 'default' ) {
			setIsDismissed(true);
		}
	};

	// Nothing to ask for: the browser can't do it, we already have it, or the
	// user told us to stop asking.
	if( !isSupported || permission === 'granted' || isDismissed ) {
		return null;
	}

	const isBlocked = permission === 'denied';

	return (
		<Prompt data-testid="desktop-notifications-prompt">
			<Icon use="notifications" size={1.25} color={isBlocked ? 'silver-base' : 'blue-base'} />

			<PromptBody>
				<PromptTitle>
					{ isBlocked ? 'Desktop notifications are blocked' : 'Turn on desktop notifications' }
				</PromptTitle>

				<PromptText>
					{ isBlocked
						? 'Allow notifications for this site in your browser settings to get alerts again.'
						: 'Get alerted about orders, workflow and embargoes even when Pixwel is not the active tab.' }
				</PromptText>

				{ !isBlocked && (
					<Button
						variant="primary"
						size="sm"
						disabled={isRequesting}
						loading={isRequesting}
						onClick={handleEnable}
						data-testid="enable-desktop-notifications"
					>
						Enable notifications
					</Button>
				)}
			</PromptBody>

			<PromptDismiss
				type="button"
				aria-label="Dismiss"
				title="Dismiss"
				onClick={() => setIsDismissed(true)}
				data-testid="dismiss-desktop-notifications"
			>
				<Icon use="close" size={0.625} color="silver-base" />
			</PromptDismiss>
		</Prompt>
	);
}

DesktopNotificationsPrompt.propTypes = {
	permission: PropTypes.string,
	isSupported: PropTypes.bool,
	isRequesting: PropTypes.bool,
	onEnable: PropTypes.func
};

export default DesktopNotificationsPrompt;
